import { AfterViewInit, Component, effect, inject } from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem, MessageService, SharedModule } from 'primeng/api';
import { History } from '../../../models/history';
import { HistoryStore } from '../../../stores/history/history.state';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { BadgeModule } from 'primeng/badge';
import { SidebarModule } from 'primeng/sidebar';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { Table, TableModule } from 'primeng/table';
import { DatePipe } from '@angular/common';
import { UserStore } from '../../../stores/user/user.state';
import { addDays } from 'date-fns';
import { getState } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-history-list',
  standalone: true,
  imports: [
    BadgeModule,
    SharedModule,
    SidebarModule,
    TableCaptionComponent,
    TableModule,
    DatePipe,
    TranslatePipe,
  ],

  templateUrl: './history-list.component.html',
  styleUrl: './history-list.component.scss',
})
export class HistoryListComponent
  extends BaseListComponent<History>
  implements AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-10',
    icon: 'fas fa-history',
    headerText: 'History',
  };

  readonly store = inject(HistoryStore);
  readonly userStore = inject(UserStore);
  override menuItems: MenuItem[] = [];

  items: History[] = []; // Your actual data
  filteredItems: History[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private translate: TranslateService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect(() => {
      const historyState = getState(this.store);
      this.items = this.store.asArray();
      if (this.store.initialized()) {
        this.filteredItems = this.items;
      }
    });

    this.translate.setDefaultLang('en'); // Set default language
    this.translate.use('de'); // Use German for translations
  }

  /**
   * A lifecycle hook called after Angular has fully initialized a component's view.
   * This method is used to configure the sidebar with the given configuration
   * and initialize the items list from the store, as well as set up the filtered items.
   *
   * @return {void} Does not return a value.
   */
  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
    this.items = this.store.asArray(); // ORDER!!
    this.filteredItems = this.items;
  }

  /**
   * Applies a date filter on the list of items based on `fromDate` and/or `toDate`.
   * Filters the `items` list and updates the `filteredItems` property with items
   * that have a `createdAt` timestamp within the specified date range.
   * If no `fromDate` or `toDate` is defined, all items are included in `filteredItems`.
   *
   * @return {void} Does not return a value. Updates `filteredItems` based on the applied date filter.
   */
  applyDateFilter(): void {
    console.log(this.fromDate, this.toDate);
    if (this.fromDate || this.toDate) {
      this.filteredItems = this.items.filter((item: History) => {
        if (item.createdAt) {
          const itemDate = new Date(item.createdAt * 1000);
          const isAfterFromDate = this.fromDate
            ? itemDate >= this.fromDate
            : true;
          const isBeforeToDate = this.toDate
            ? itemDate <= addDays(this.toDate, 1)
            : true;
          return isAfterFromDate && isBeforeToDate;
        }
        return false;
      });
    } else {
      this.filteredItems = this.items;
    }
  }

  /**
   * Handles the change event for the 'from date' field, updating the internal state and applying the date filter accordingly.
   *
   * @param {Date|null} date - The selected 'from date'. Can be null to indicate no date is selected.
   * @return {void} This method does not return a value.
   */
  onFromDateChange(date: Date | null): void {
    this.fromDate = date;
    this.applyDateFilter();
  }

  /**
   * Handles changes to the "toDate" property by updating its value and applying the date filter logic.
   *
   * @param {Date | null} date - The new date value to be set for the "toDate" property or null if no date is selected.
   * @return {void} This method does not return a value.
   */
  onToDateChange(date: Date | null): void {
    this.toDate = date;
    this.applyDateFilter();
  }

  /**
   * Resets the provided table and related filter parameters to their initial state.
   *
   * @param {Table} table - The table instance to be cleared.
   * @return {void} Does not return a value.
   */
  override clear(table: Table): void {
    table.clear();
    this.searchWord = '';
    this.fromDate = null;
    this.toDate = null;
    this.filteredItems = this.items;
  }
}
